import { Flex, Group, Button, Menu, Text, Box, Modal, ActionIcon } from '@mantine/core';
import { NavLink, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { IconLink, IconDots, IconArrowLeft, IconX } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useEvent } from 'src/meetings/hooks/useEvent';
import { useDeleteCalendarEventMutation } from 'src/meetings/hooks/useDeleteCalendarEventMutation';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { EventTimeCard } from 'src/meetings/detail/components/EventTimeCard';
import { EventInfoRow } from 'src/meetings/detail/components/EventInfoRow';
import { MeetingTabs } from 'src/meetings/detail/components/MeetingTabs';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import PageLoader from 'src/UI/loader/PageLoader';
import { SummaryTab } from './tabs/SummaryTab';
import { MeetingNoteTab } from './tabs/MeetingNoteTab';
import { TranscriptTab } from './tabs/TranscriptTab';
import { PreMeetingTab } from './tabs/PreMeetingTab';
import { ComplianceTab } from './tabs/ComplianceTab';
import { useFlags } from 'src/feature-flags/FlagsHook';

type Props = {
  eventId: string;
  onDelete: () => void;
  basePath: string;
  onClose?: () => void;
  tab?: string;
};

export const MeetingDetailsContent = ({ eventId, onDelete, basePath, onClose, tab }: Props) => {
  const { event, isLoading } = useEvent(eventId!);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { deleteCalendarEvent } = useDeleteCalendarEventMutation();
  const { flagEnabledComplianceChecking } = useFlags();
  const navigate = useNavigate();

  useEffect(() => {
    if (!tab) {
      navigate(`${basePath}/summary`, { replace: true });
    }
  }, [tab, eventId, navigate]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      notifications.show({
        title: 'Link copied',
        message: 'Meeting link has been copied to clipboard',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Failed to copy link',
        message: 'Please try again',
        color: 'red',
      });
    }
  };

  const handleDeleteEvent = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteCalendarEvent(eventId!);
      setShowDeleteModal(false);
      onDelete();
    } catch (error) {
      notifyAndLogError(
        'Failed to delete event',
        'An error occurred while trying to delete the event. Please try again later.',
        error,
        { eventId },
      );
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (!event) {
    return <Box>Event not found</Box>;
  }

  const getTabContent = () => {
    switch (tab) {
      case 'summary':
        return <SummaryTab event={event} />;
      case 'meeting-note':
        return <MeetingNoteTab event={event} />;
      case 'transcript':
        return <TranscriptTab event={event} />;
      case 'pre-meeting':
        return <PreMeetingTab event={event} />;
      case 'compliance':
        return flagEnabledComplianceChecking ? (
          <ComplianceTab event={event} />
        ) : (
          <SummaryTab event={event} />
        );
      default:
        return <SummaryTab event={event} />;
    }
  };

  return (
    <Box>
      {!onClose && (
        <Flex justify="space-between">
          <NavLink to="/meetings">
            <IconArrowLeft />
          </NavLink>
          <Group gap="xs">
            <Button
              variant="secondary"
              rightSection={<IconLink size={16} />}
              onClick={handleCopyLink}
            >
              Copy link
            </Button>
            <Menu position="bottom-end">
              <Menu.Target>
                <Button variant="secondary" px="xs">
                  <IconDots size={16} />
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={handleDeleteEvent}>Delete event</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Flex>
      )}

      <Box mt="sm">
        <Flex justify="space-between">
          <EventTimeCard
            scheduledStartAt={event.scheduledStartAt}
            scheduledEndAt={event.scheduledEndAt}
            startAt={event.startAt}
            endAt={event.endAt}
          />

          {onClose && (
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={e => {
                e.stopPropagation();
                onClose();
              }}
            >
              <IconX size={20} />
            </ActionIcon>
          )}
        </Flex>
      </Box>

      <Box mt="xs">
        <Text size="displaySm" fw={FONT_WEIGHTS.semibold} c="gray.10">
          {event.title}
        </Text>
      </Box>

      <Box mt="xxs">
        <EventInfoRow event={event} />
      </Box>

      <Box mt="lg">
        <MeetingTabs currentTab={tab} basePath={basePath} />
      </Box>

      <Box mt="xxl">{getTabContent()}</Box>

      <Modal
        opened={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete event"
        centered
      >
        <Text mb="md">
          Are you sure you want to delete {event.title}? This action cannot be undone.
        </Text>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Group>
      </Modal>
    </Box>
  );
};
